<template>
  <div v-if="videoDetailsReady">
    <div class="content-with-spacing">
      <div class="heading-container">
        <h1 v-if="videoTitle">{{ videoTitle }}</h1>
        <div class="heading-buttons">
          <module-asset-completion-button :asset="asset"></module-asset-completion-button>
          <intersection-observer observed-element="completion-buttons"
            @on-intersection-element="onElementInView"
          ></intersection-observer>
        </div>
      </div>
      <div class="video-container panel panel-default">
        <div v-if="!detectBrowser.isChrome && !detectBrowser.isSafari" class="category-header">
          <span class="pip-info">
            To access picture-in-picture mode, right-click over the video and select the "Picture-in-Picture" option.
          </span>
        </div>
        <k-video
          :moduleId="moduleId"
          :video="asset"
          :moduleVideoId="moduleVideoId"
          :supportsRangeHeaders="isRangeHeaderSupported"
          :hasTranscript="Boolean(asset.transcript_link)"
          @ready="(r) => videoReady = r"
        />
      </div>
      <div class="bottom-button-container" v-if="!isInViewElement">
        <module-asset-completion-button :asset="asset"></module-asset-completion-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.heading-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.video-container {
  padding: 15px;
}

.category-header {
  margin-bottom: 20px;
}
</style>

<script>
import IntersectionObserver from '@base-components/k-intersection-observer.vue';
import KVideo from './k-video.vue';
import ModuleAssetCompletionButton from './module-asset-completion-button.vue';

export default {
  components: {
    KVideo,
    ModuleAssetCompletionButton,
    IntersectionObserver,
  },

  props: {
    moduleDetails: {
      type: Object,
    },
    asset: {
      type: Object,
    },
    sidebarReady: {
      type: Boolean,
    },
  },

  data() {
    return {
      isInViewElement: false,
      videoReady: false,
      isRangeHeaderSupported: null, // null = to be determined
    };
  },

  beforeMount() {
    this.$Loading.start();
  },

  watch: {
    assetId: {
      handler(newId, oldId) {
        // Only check if we have a new ID and it's different from the old one
        if (newId && newId !== oldId) {
          // Reset the range header support check for new asset
          this.isRangeHeaderSupported = null;
          this.checkRangeHeaderSupport();
        }
      },
      immediate: true,
    },
    ready() {
      if (this.ready) {
        this.$Loading.finish();
      } else {
        this.$Loading.start();
      }
    },
    asset() {
      this.videoReady = false;
    },
  },

  computed: {
    ready() {
      return Boolean(this.moduleDetails && this.asset && this.videoReady && this.sidebarReady);
    },

    assetId() {
      return this.asset?.id;
    },

    videoDetailsReady() {
      return Boolean(this.moduleDetails && this.asset);
    },

    moduleId() {
      return parseInt(this.$route.params.moduleId, 10);
    },

    videoTitle() {
      return this.asset?.name;
    },

    moduleVideoId() {
      return parseInt(this.$route.params.moduleVideoId, 10);
    },
  },

  methods: {
    onElementInView(value) {
      this.isInViewElement = value;
    },

    async checkRangeHeaderSupport() {
      try {
        await this.$http.get(
          `/api/static/video/${this.assetId}/watch`,
          { headers: { Range: 'bytes=0-1' } },
        );
        this.isRangeHeaderSupported = true;
      } catch (err) {
        this.isRangeHeaderSupported = false;
        this.$logger.warn('Range header test failed, using signed URLs', undefined, err);
      }
    },
  },

};
</script>
